import { buildIndex } from './localTree';

/**
 * Builds API responses from the survey tree kept on the device: offline, or
 * while changes are waiting to sync, or for records created on this device
 * (whose ids are client_id UUIDs the server doesn't know yet).
 *
 * GET /surveys returns the whole nested tree (survey -> transects -> quadrats
 * -> trees / regeneration plots / disturbances) in the same shape as the
 * detail endpoints, which only add their parents: a transect carries
 * `survey`, a quadrat carries `transect` (with its `survey`). So one tree is
 * enough to answer every record request. Ids in paths may be server ids or
 * client_ids.
 */

const scalars = (record) =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => !Array.isArray(value) && (value === null || typeof value !== 'object')));

const transectWithSurvey = ({ node, parent }) => ({ ...node, survey: parent ? scalars(parent) : undefined });

const quadratWithParents = (index, { node, parent }) => {
  const survey = parent ? index.transect.get(String(parent.id))?.parent : null;
  return { ...node, transect: parent ? { ...scalars(parent), survey: survey ? scalars(survey) : undefined } : undefined };
};

const all = (index, type) => [...new Set([...index[type].values()].map((entry) => entry.node))];

// [pattern, (ref, index) => data | undefined]
const ROUTES = [
  [/^\/surveys\/([^/]+)$/, (ref, i) => i.survey.get(ref)?.node],
  [/^\/surveys\/([^/]+)\/transects$/, (ref, i) => i.survey.get(ref)?.node.transects],
  [/^\/transects\/([^/]+)$/, (ref, i) => (i.transect.has(ref) ? transectWithSurvey(i.transect.get(ref)) : undefined)],
  [/^\/transects\/([^/]+)\/quadrats$/, (ref, i) => i.transect.get(ref)?.node.quadrats],
  [/^\/quadrats$/, (_, i) => all(i, 'quadrat')],
  [/^\/quadrats\/([^/]+)$/, (ref, i) => (i.quadrat.has(ref) ? quadratWithParents(i, i.quadrat.get(ref)) : undefined)],
  [/^\/quadrats\/([^/]+)\/mature-trees$/, (ref, i) => i.quadrat.get(ref)?.node.mature_trees],
  [/^\/quadrats\/([^/]+)\/regeneration-plots$/, (ref, i) => i.quadrat.get(ref)?.node.regeneration_plots],
  [/^\/quadrats\/([^/]+)\/disturbances$/, (ref, i) => i.quadrat.get(ref)?.node.disturbances],
  [/^\/mature-trees$/, (_, i) => all(i, 'mature_tree')],
  [/^\/mature-trees\/([^/]+)$/, (ref, i) => i.mature_tree.get(ref)?.node],
  [/^\/regeneration-plots\/([^/]+)$/, (ref, i) => i.regeneration_plot.get(ref)?.node],
  [/^\/regeneration-plots\/([^/]+)\/regeneration-counts$/, (ref, i) => i.regeneration_plot.get(ref)?.node.regeneration_counts],
  [/^\/regeneration-counts$/, (_, i) => all(i, 'regeneration_count')],
  [/^\/regeneration-counts\/([^/]+)$/, (ref, i) => i.regeneration_count.get(ref)?.node],
  [/^\/disturbances$/, (_, i) => all(i, 'disturbance')],
  [/^\/disturbances\/([^/]+)$/, (ref, i) => i.disturbance.get(ref)?.node],
];

/** True if `path` is a field-record request this module can answer. */
export const isRecordPath = (path) => path === '/surveys' || ROUTES.some(([pattern]) => pattern.test(path));

/** The response for `path` built from `surveys` (the tree), or undefined. */
export const deriveFromSurveys = (path, surveys) => {
  if (!Array.isArray(surveys)) return undefined;
  if (path === '/surveys') return surveys;
  const route = ROUTES.find(([pattern]) => pattern.test(path));
  if (!route) return undefined;
  const [, ref] = path.match(route[0]);
  return route[1](ref === undefined ? undefined : decodeURIComponent(ref), buildIndex(surveys));
};
